<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Dropdown, DropdownMenu, DropdownToggle, TabContent, TabPane, Tooltip } from '@sveltestrap/sveltestrap';

  import { DateFilter } from '@client/components/filters/date';
  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { TagBlocksService } from '@client/services/tag-blocks';

  import { usersSettingsStore } from '@client/stores/page-settings';
  import { syncConnections } from '@client/stores/sync-connections';

  import { QueryParams } from '@client/utils/query-params';

  import { debounce } from '@common/utils/debounce';

  import { GroupFilter } from './group-filter';

  export let initialized = false;
  export let params = {};

  const groupEntries = {
    csvtreenodebindings: 'workdayManagers',
    googlecountrybindings: 'googleCountries',
    googlegroupbindings: 'googleGroups',
    o365groupbindings: 'M365Groups',
    oktagroupbindings: 'oktaGroups',
    exclusions: {
      googlegroupbindings: 'excludedGoogleGroups',
      o365groupbindings: 'excludedM365Groups',
      oktagroupbindings: 'excludedOktaGroups'
    }
  };

  let blank = '';
  let blockItems = [];
  let selectedBlockName = params.block;

  let fieldName = params.fieldname;
  let fieldRegex = params.fieldregex;
  let userParamName = params.userparamname;
  let userParamRegex = params.userparamregex;

  let keyword = params.keyword;
  let imgsrc = params.imgsrc;

  const buildTriStateItems = (trueLabel, falseLabel) => [
    { label: $format('label.ANY'), value: null },
    { label: $format(trueLabel), value: true },
    { label: $format(falseLabel), value: false }
  ];

  const findTriStateItem = (items, value) => items.find((item) => item.value === (value ?? null)) ?? items[0];

  let isEmptyItems = buildTriStateItems('label.EMPTY_ONLY', 'label.NOT_EMPTY_ONLY');
  let imagesItems = buildTriStateItems('label.HAS_IMAGES', 'label.NO_IMAGES');
  let linksItems = buildTriStateItems('label.HAS_LINKS', 'label.NO_LINKS');
  let hasHttpImagesItems = buildTriStateItems('label.HAS_HTTP_IMAGES', 'label.NO_HTTP_IMAGES');
  let imgSrcCheckItems = buildTriStateItems('label.IMG_SRC_MATCHES', 'label.IMG_SRC_EXCLUDES');

  let selectedIsEmpty = findTriStateItem(isEmptyItems, params.isEmpty);
  let selectedImages = findTriStateItem(imagesItems, params.images);
  let selectedLinks = findTriStateItem(linksItems, params.links);
  let selectedHasHttpImages = findTriStateItem(hasHttpImagesItems, params.hasHttpImages);
  let selectedImgSrcCheck = findTriStateItem(imgSrcCheckItems, params.imgSrcCheck);

  const clearAdvancedFilters = () => {
    params = {
      ...params,
      fieldname: '',
      fieldregex: '',
      userparamname: '',
      userparamregex: '',
      block: '',
      notblock: false,
      csvtreenodebindings: [],
      googlecountrybindings: [],
      googlegroupbindings: [],
      o365groupbindings: [],
      oktagroupbindings: [],
      exclusions: {
        ...params.exclusions,
        googlegroupbindings: [],
        o365groupbindings: [],
        oktagroupbindings: []
      },
      keyword: '',
      isEmpty: null,
      images: null,
      links: null,
      hasHttpImages: null,
      imgsrc: '',
      imgSrcCheck: null,
      skewedImages: false,
      filterByGoogleDate: false
    };

    selectedBlockName = '';
    fieldName = '';
    fieldRegex = '';
    userParamName = '';
    userParamRegex = '';

    keyword = '';
    imgsrc = '';
    selectedIsEmpty = isEmptyItems[0];
    selectedImages = imagesItems[0];
    selectedLinks = linksItems[0];
    selectedHasHttpImages = hasHttpImagesItems[0];
    selectedImgSrcCheck = imgSrcCheckItems[0];
  };

  const clearBlockName = () => {
    params = {
      ...params,
      block: ''
    };

    if (params.notblock) {
      params.notblock = false;
    }
  };

  const onInputDebouce = debounce((param, value) => {
    params[param] = value;
  }, 300);

  const onChange = (items, field, exclusion) => {
    if (!items.length) {
      if (exclusion) {
        params = {
          ...params,
          exclusions: {
            ...params.exclusions,
            [field]: []
          }
        };

        $usersSettingsStore.params.exclusions[field] = params.exclusions[field];
      } else {
        params = {
          ...params,
          [field]: []
        };

        $usersSettingsStore.params[field] = params[field];
      }
    } else {
      if (exclusion) {
        params.exclusions[field] = items.map((item) => ({ id: item.id, name: item.name }));
        $usersSettingsStore.params.exclusions[field] = params.exclusions[field];
      } else {
        params[field] = items.map((item) => ({ id: item.id, name: item.name }));
        $usersSettingsStore.params[field] = params[field];
      }
    }
  };

  onMount(async () => {
    initialized = false;

    const blocks = await TagBlocksService.getAll();

    let selected = null;

    blockItems = blocks
      .map(({ rulename }) => ({
        label: rulename,
        value: rulename
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    initialized = true;
  });

  const joinQueryParams = (param, key) => {
    return param.map((item) => item?.[key]).join(',');
  };

  const updateQueryParams = () => {
    const entries = [
      ['block', selectedBlockName?.value],
      ['fieldname', fieldName],
      ['fieldregex', fieldRegex],
      ['userparamname', userParamName],
      ['userparamregex', userParamRegex],
      ['notblock', params.notblock],
      ['workdayManagers', joinQueryParams(params.csvtreenodebindings, 'name')],
      ['googleCountries', joinQueryParams(params.googlecountrybindings, 'name')],
      ['googleGroups', joinQueryParams(params.googlegroupbindings, 'name')],
      ['M365Groups', joinQueryParams(params.o365groupbindings, 'name')],
      ['oktaGroups', joinQueryParams(params.oktagroupbindings, 'name')],
      ['excludedGoogleGroups', joinQueryParams(params.exclusions.googlegroupbindings, 'name')],
      ['excludedM365Groups', joinQueryParams(params.exclusions.o365groupbindings, 'name')],
      ['excludedOktaGroups', joinQueryParams(params.exclusions.oktagroupbindings, 'name')]
    ];

    for (const [key, value] of entries) {
      if (value) {
        QueryParams.set(key, value);
      } else {
        QueryParams.delete(key);
      }
    }
  };

  $: groupBindingsHaveNoLength =
    !params.csvtreenodebindings.length &&
    !params.googlecountrybindings.length &&
    !params.googlegroupbindings.length &&
    !params.o365groupbindings.length &&
    !params.oktagroupbindings.length &&
    !params.exclusions.googlegroupbindings.length &&
    !params.exclusions.o365groupbindings.length &&
    !params.exclusions.oktagroupbindings.length;

  $: isDisabled =
    !params.block &&
    !params.notblock &&
    !params.fieldname &&
    !params.userparamname &&
    groupBindingsHaveNoLength &&
    params.keyword === '' &&
    params.isEmpty === null &&
    params.images === null &&
    params.links === null &&
    params.hasHttpImages === null &&
    params.imgSrcCheck === null &&
    params.imgsrc === '' &&
    !params.skewedImages &&
    !params.filterByGoogleDate;

  $: hasGoogle = $syncConnections.checkgappsconnected;
  $: hasO365 = $syncConnections.checko365connected;
  $: hasOkta = $syncConnections.checkoktaconnected;
  $: hasGroupSource = hasGoogle || hasO365 || hasOkta;

  $: updateQueryParams(
    params.block,
    params.fieldname,
    params.userparamname,
    params.notblock,
    params.fieldname,
    params.fieldregex,
    params.csvtreenodebindings,
    params.googlecountrybindings,
    params.googlegroupbindings,
    params.o365groupbindings,
    params.oktagroupbindings,
    params.exclusions.googlegroupbindings,
    params.exclusions.o365groupbindings,
    params.exclusions.oktagroupbindings
  );
</script>

<Dropdown autoClose="outside">
  <DropdownToggle tag="div">
    <div class="filter-toggle" class:active={!isDisabled}>
      <span role="button" tabindex="0">
        <Icon name="filter" class="pointer text-body-secondary" size={15} />
        {$format('label.ADVANCED')}
        {#if !isDisabled}
          <div class="clear-icon" on:click|stopPropagation={clearAdvancedFilters} role="button" tabindex="0">
            <Icon name="x" />
          </div>
        {/if}
      </span>
    </div>
  </DropdownToggle>
  <DropdownMenu>
    <TabContent>
      <TabPane tabId="users" tab={$format('label.USERS')} active>
        <div class="advanced-filters">
          <div class="section">
            <div class="section-info">
              <h5>
                {$format('label.FILTER_BY.FIELD')}
                <Icon id="fields-info-tooltip" name="help-circle" />
                <Tooltip target="fields-info-tooltip">{$format('label.FILTER_BY_TOOLTIP.FIELD')}</Tooltip>
              </h5>
            </div>
            <div class="section-controls">
              <input
                bind:value={fieldName}
                on:input={() => onInputDebouce('fieldname', fieldName)}
                placeholder={$format('label.PLACEHOLDER.INPUT', {
                  values: { param: $format('label.NAME').toLowerCase() }
                })}
              />
              <input
                bind:value={fieldRegex}
                on:input={() => onInputDebouce('fieldregex', fieldRegex)}
                placeholder={$format('label.PLACEHOLDER.INPUT', {
                  values: { param: $format('label.VALUE').toLowerCase() }
                })}
              />
            </div>
          </div>
          <div class="section">
            <div class="section-info">
              <h5>
                {$format('label.FILTER_BY.USER_PARAM')}
                <Icon id="params-info-tooltip" name="help-circle" />
                <Tooltip target="params-info-tooltip">
                  {$format('label.FILTER_BY_TOOLTIP.USER_PARAM')}
                </Tooltip>
              </h5>
            </div>
            <div class="section-controls">
              <input
                bind:value={userParamName}
                on:input={() => onInputDebouce('userparamname', userParamName)}
                placeholder={$format('label.PLACEHOLDER.INPUT', {
                  values: { param: $format('label.NAME').toLowerCase() }
                })}
              />
              <input
                bind:value={userParamRegex}
                on:input={() => onInputDebouce('userparamregex', userParamRegex)}
                placeholder={$format('label.PLACEHOLDER.INPUT', {
                  values: { param: $format('label.VALUE').toLowerCase() }
                })}
              />
            </div>
          </div>
          <div class="section">
            <div class="section-info">
              <h5>
                {$format('label.FILTER_BY.SIGNATURE')}
                <Icon id="block-info-tooltip" name="help-circle" />
                <Tooltip target="block-info-tooltip">{$format('label.FILTER_BY_TOOLTIP.SIGNATURE')}</Tooltip>
              </h5>
            </div>
            <div class="section-controls block">
              <Select
                bind:items={blockItems}
                bind:value={selectedBlockName}
                on:change={() => {
                  params.block = selectedBlockName.value;
                }}
                on:clear={clearBlockName}
                placeholder={$format('label.PLACEHOLDER.SELECT_BLOCK_NAME')}
                showChevron={!selectedBlockName}
                searchable
              />
              <span>
                <h5>
                  {$format('label.EXCLUDE_SIGNATURE')}
                  <Icon id="complement-info-tooltip" name="help-circle" />
                  <Tooltip target="complement-info-tooltip">
                    {$format('label.TOOLTIP.EXCLUDE_SIGNATURE')}
                  </Tooltip>
                </h5>
                <Switch bind:checked={params.notblock} />
              </span>
            </div>
          </div>
        </div>

      </TabPane>
      {#if hasGroupSource}
        <TabPane tabId="groups" tab={$format('label.GROUPS')}>
          <div class="advanced-filters">
            {#if hasGoogle}
              {#key !params.googlecountrybindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GOOGLE_COUNTRIES')}
                  tooltipId="countries-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GOOGLE_COUNTRIES')}
                  field="googlecountrybindings"
                  bind:config={params}
                  {onChange}
                />
              {/key}

              {#key !params.googlegroupbindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GROUPS_INCLUDED', {
                    values: { company: $format('label.GOOGLE') }
                  })}
                  tooltipId="google-group-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GROUPS_INCLUDED', {
                    values: { company: $format('label.GOOGLE') }
                  })}
                  field="googlegroupbindings"
                  bind:config={params}
                  {onChange}
                />
              {/key}

              {#key !params.exclusions.googlegroupbindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GROUPS_EXCLUDED', {
                    values: { company: $format('label.GOOGLE') }
                  })}
                  tooltipId="google-group-excluded-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GROUPS_EXCLUDED', {
                    values: { company: $format('label.GOOGLE') }
                  })}
                  field="googlegroupbindings"
                  exclusions={true}
                  bind:config={params}
                  {onChange}
                />
              {/key}

              {#key !params.csvtreenodebindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.WORKDAY_MANAGERS')}
                  tooltipId="workday-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.WORKDAY_MANAGERS')}
                  field="csvtreenodebindings"
                  isLastChild={hasGoogle && !hasO365 && !hasOkta}
                  bind:config={params}
                  {onChange}
                />
              {/key}
            {/if}

            {#if hasO365}
              {#key !params.o365groupbindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GROUPS_INCLUDED', {
                    values: { company: $format('label.M365') }
                  })}
                  tooltipId="o365-group-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GROUPS_INCLUDED', {
                    values: { company: $format('label.M365') }
                  })}
                  field="o365groupbindings"
                  bind:config={params}
                  {onChange}
                />
              {/key}

              {#key !params.exclusions.o365groupbindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GROUPS_EXCLUDED', {
                    values: { company: $format('label.M365') }
                  })}
                  tooltipId="o365-group-excluded-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GROUPS_EXCLUDED', {
                    values: { company: $format('label.M365') }
                  })}
                  field="o365groupbindings"
                  exclusions={true}
                  isLastChild={hasO365 && !hasOkta}
                  bind:config={params}
                  {onChange}
                />
              {/key}
            {/if}

            {#if hasOkta}
              {#key !params.oktagroupbindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GROUPS_INCLUDED', {
                    values: { company: $format('label.OKTA') }
                  })}
                  tooltipId="okta-included-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GROUPS_INCLUDED', {
                    values: { company: $format('label.OKTA') }
                  })}
                  field="oktagroupbindings"
                  bind:config={params}
                  {onChange}
                />
              {/key}

              {#key !params.exclusions.oktagroupbindings.length}
                <GroupFilter
                  title={$format('label.FILTER_BY.GROUPS_EXCLUDED', {
                    values: { company: $format('label.OKTA') }
                  })}
                  tooltipId="okta-excluded-info-tooltip"
                  tooltipText={$format('label.FILTER_BY_TOOLTIP.GROUPS_EXCLUDED', {
                    values: { company: $format('label.OKTA') }
                  })}
                  field="oktagroupbindings"
                  exclusions={true}
                  isLastChild={hasOkta}
                  bind:config={params}
                  {onChange}
                />
              {/key}
            {/if}
          </div>

        </TabPane>
      {/if}
      {#if hasGoogle}
        <TabPane tabId="gsuite" tab={$format('label.GSUITE')}>
          <div class="advanced-filters">
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.SIGNATURE_CONTENT')}
                  <Icon id="signature-content-info-tooltip" name="help-circle" />
                  <Tooltip target="signature-content-info-tooltip">
                    {$format('label.FILTER_BY_TOOLTIP.SIGNATURE_CONTENT')}
                  </Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <input
                  bind:value={keyword}
                  on:input={() => onInputDebouce('keyword', keyword)}
                  placeholder={$format('label.SEARCH')}
                />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.EMPTY_STATE')}
                  <Icon id="empty-state-info-tooltip" name="help-circle" />
                  <Tooltip target="empty-state-info-tooltip">
                    {$format('label.FILTER_BY_TOOLTIP.EMPTY_STATE')}
                  </Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <Select
                  bind:items={isEmptyItems}
                  bind:value={selectedIsEmpty}
                  on:change={() => {
                    params.isEmpty = selectedIsEmpty?.value ?? null;
                  }}
                  clearable={false}
                  showChevron
                />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.IMAGES')}
                  <Icon id="images-info-tooltip" name="help-circle" />
                  <Tooltip target="images-info-tooltip">{$format('label.FILTER_BY_TOOLTIP.IMAGES')}</Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <Select
                  bind:items={imagesItems}
                  bind:value={selectedImages}
                  on:change={() => {
                    params.images = selectedImages?.value ?? null;
                  }}
                  clearable={false}
                  showChevron
                />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.LINKS')}
                  <Icon id="links-info-tooltip" name="help-circle" />
                  <Tooltip target="links-info-tooltip">{$format('label.FILTER_BY_TOOLTIP.LINKS')}</Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <Select
                  bind:items={linksItems}
                  bind:value={selectedLinks}
                  on:change={() => {
                    params.links = selectedLinks?.value ?? null;
                  }}
                  clearable={false}
                  showChevron
                />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.HTTP_IMAGES')}
                  <Icon id="http-images-info-tooltip" name="help-circle" />
                  <Tooltip target="http-images-info-tooltip">
                    {$format('label.FILTER_BY_TOOLTIP.HTTP_IMAGES')}
                  </Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <Select
                  bind:items={hasHttpImagesItems}
                  bind:value={selectedHasHttpImages}
                  on:change={() => {
                    params.hasHttpImages = selectedHasHttpImages?.value ?? null;
                  }}
                  clearable={false}
                  showChevron
                />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.IMG_SRC')}
                  <Icon id="img-src-info-tooltip" name="help-circle" />
                  <Tooltip target="img-src-info-tooltip">{$format('label.FILTER_BY_TOOLTIP.IMG_SRC')}</Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <input
                  bind:value={imgsrc}
                  on:input={() => onInputDebouce('imgsrc', imgsrc)}
                  placeholder={$format('label.SEARCH')}
                />
                <Select
                  bind:items={imgSrcCheckItems}
                  bind:value={selectedImgSrcCheck}
                  on:change={() => {
                    params.imgSrcCheck = selectedImgSrcCheck?.value ?? null;
                  }}
                  clearable={false}
                  showChevron
                />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.SKEWED_IMAGE')}
                  <Icon id="skewed-image-info-tooltip" name="help-circle" />
                  <Tooltip target="skewed-image-info-tooltip">
                    {$format('label.FILTER_BY_TOOLTIP.SKEWED_IMAGE')}
                  </Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <Switch bind:checked={params.skewedImages} />
              </div>
            </div>
            <div class="section">
              <div class="section-info">
                <h5>
                  {$format('label.FILTER_BY.GSUITE_CREATION_DATE')}
                  <Icon id="gsuite-date-info-tooltip" name="help-circle" />
                  <Tooltip target="gsuite-date-info-tooltip">
                    {$format('label.FILTER_BY_TOOLTIP.GSUITE_CREATION_DATE')}
                  </Tooltip>
                </h5>
              </div>
              <div class="section-controls">
                <Switch bind:checked={params.filterByGoogleDate} />
                {#if params.filterByGoogleDate && params.googleDateRange}
                  <DateFilter
                    bind:startDate={params.googleDateRange.startDate}
                    bind:endDate={params.googleDateRange.endDate}
                    isRange
                  />
                {/if}
              </div>
            </div>
          </div>

        </TabPane>
      {/if}
    </TabContent>
  </DropdownMenu>
</Dropdown>

<style lang="scss" src="./advanced-filter.scss"></style>
